import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type SyntheticEvent,
} from "react";
import { useAtom } from "jotai";
import { EnvelopeOpenIcon, XIcon } from "@phosphor-icons/react";
import {
  emailAtom,
  emailValidAtom,
  messageForLoginModalAtom,
  registeredUserIdAtom,
  resendAvailableUntilAtom,
  resendAvailableUntil,
  secondsUntil,
  SUBMIT_ACCESSIBILITY_DEFAULT,
  submitAccessibilityAtom,
  verificationMethodAtom,
  verifyEmailVariantAtom,
} from "@/store/atoms";
import Loader from "@/components/common/Loader";
import {
  useAccessibility,
  useCountdown,
  useI18N,
  useResendVerifyEmail,
  useVerifyEmail,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import { hasVerifiedSession, isVerifyEmailCodeError } from "common/apis";
import {
  createPageAnalytics,
  setUserProperties,
  trackEvent,
} from "common/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { useCustomToast } from "@/hooks/useCustomToast";
import { useDarkMode } from "@/hooks/useDarkMode";
import WideButton from "@/components/common/WideButton";
import { StandardButton } from "@/components/common/StandardButton";
import VerificationCodeInput, {
  VERIFICATION_CODE_LENGTH,
} from "@/components/common/VerificationCodeInput";
import { referralSourceLabel } from "@/features/auth/referralSourceLabel";
import { useAfterLogin } from "@/features/auth/useAfterLogin";
import { rememberPendingVerification } from "@/features/auth/pendingVerification";
import { entryPageFromCurrentPage } from "@/analytics/entryPage";

/** `error_type` for `email_verification_failed`, from the backend's error code. */
function verificationErrorType(error: unknown): string {
  if (!isVerifyEmailCodeError(error)) return "network_error";
  if (error.code === "code_expired") return "expired_code";
  if (error.code === "too_many_attempts") return "too_many_attempts";
  return "incorrect_code";
}

/**
 * Post-sign-up / unverified-login screen (DEV-602). Figma 16232:296324 (sign-up,
 * no header bar) and 16150:269332 (login, header with close). In code mode the
 * user types the 6-digit code here; in link mode the copy points at the inbox.
 */
const VerifyEmailComponent = () => {
  const isZoomedIn = useZoomDetection();
  const isDark = useDarkMode();
  const i18n = useI18N();
  const showToast = useCustomToast();
  const { setScreenIndex, hideModal, setBusy } = useAuthModals();
  const { modalStyle } = useModalHeight();

  const [email] = useAtom(emailAtom);
  const [, setEmailValid] = useAtom(emailValidAtom);
  const [, setMessageForLoginModal] = useAtom(messageForLoginModalAtom);
  const [method] = useAtom(verificationMethodAtom);
  const [variant] = useAtom(verifyEmailVariantAtom);
  const [resendAvailableUntilValue, setResendAvailableUntil] = useAtom(
    resendAvailableUntilAtom,
  );
  const [registeredUserId, setRegisteredUserId] = useAtom(registeredUserIdAtom);
  const [submitData, setSubmitData] = useAtom(submitAccessibilityAtom);

  const isSignup = variant === "signup";
  const isCodeMode = method === "code";

  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | undefined>();
  // Seconds are derived once per expiry so useCountdown restarts only when the backend
  // hands over a new one; it ticks from an absolute deadline, so a backgrounded tab resumes right.
  const cooldownSeconds = useMemo(
    () => secondsUntil(resendAvailableUntilValue),
    [resendAvailableUntilValue],
  );
  const countdown = useCountdown(cooldownSeconds);

  const {
    verifyUserEmail,
    error: verifyError,
    isLoading: isVerifying,
    result: verifyResult,
  } = useVerifyEmail();

  const {
    resendVerifyUserEmail,
    error: resendError,
    isLoading: isResending,
    result: resendResult,
    reset: resetResend,
  } = useResendVerifyEmail();

  const {
    submitAccessibility,
    submitAccessibilityError,
    submitAccessibilityResponse,
    submitAccessibilityStatus,
    filterAccessibility,
    howDoYouHearAboutOptions,
    howDoYouHearAboutGroups,
  } = useAccessibility();

  const isSubmittingOnboarding = submitAccessibilityStatus === "pending";
  const verifyEmailAnalytics = createPageAnalytics("verify_email");
  const afterLogin = useAfterLogin();

  useEffect(() => {
    // Web has no "Open email app" button, so email_app_cta_activated is RN-only.
    verifyEmailAnalytics.track("page_view_page", {
      entry_point: isSignup ? "signup" : "login",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This browser started the verification: the email link may sign it in (and no other browser).
  useEffect(() => {
    if (email) rememberPendingVerification(email);
  }, [email]);

  // ---- Onboarding hand-off: sign-up only. The atoms are in-memory and never reset
  // elsewhere, so the login variant must not re-post the previous registrant's answers.
  useEffect(() => {
    if (isSignup && submitData && registeredUserId) {
      submitAccessibility({ ...submitData, userId: registeredUserId });
    }
  }, [isSignup, submitData, registeredUserId, submitAccessibility]);

  useEffect(() => {
    if (submitAccessibilityError) {
      console.log(
        "submitAccessibilityError",
        submitAccessibilityError?.message,
      );
    }
  }, [submitAccessibilityError]);

  // Centralized user (people) properties captured from the profile-setup wizard,
  // set once the profile update has persisted. account_creation_date/method are
  // set earlier at identify time (the account-creation moment).
  useEffect(() => {
    if (!isSignup || !submitAccessibilityResponse || !submitData) return;

    // saved_accessibility_filter: wizard stores tag ids -> map to tag names.
    const tagNameById = new Map<string, string>();
    filterAccessibility.forEach((group) => {
      group?.accessibility_tags?.forEach((tag) => {
        tagNameById.set(String(tag.id), tag.name);
      });
    });
    const savedAccessibilityFilter = (
      submitData.accessibilityFeatureRequestTagsValue || ""
    )
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean)
      .map((id) => tagNameById.get(id) ?? id);

    // referral_source: group names for a grouped sign-up, option titles for a flat one.
    const referralSource = referralSourceLabel(
      submitData,
      howDoYouHearAboutOptions,
      howDoYouHearAboutGroups,
    );

    // accessibility_profile: comma-separated accessibility-need display names.
    const accessibilityProfile = (submitData.ability || "")
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean);

    setUserProperties({
      accessibility_profile: accessibilityProfile,
      profile_visibility: submitData.makeDisabilityVisibleValue
        ? "public"
        : "private",
      saved_accessibility_filter: savedAccessibilityFilter,
      ...(referralSource ? { referral_source: referralSource } : {}),
    });

    // Consumed: a later login-variant mount must find nothing to re-post.
    setRegisteredUserId(0);
    setSubmitData(SUBMIT_ACCESSIBILITY_DEFAULT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitAccessibilityResponse]);

  // ---- Resend outcome
  useEffect(() => {
    if (!resendResult) return;
    showToast(resendResult.message, "success");
    const next = Number(resendResult.data?.resend_available_in ?? 0);
    setResendAvailableUntil(resendAvailableUntil(next));
    // The old digits belong to a credential the backend just replaced.
    setCode("");
    setCodeError(undefined);
    document.getElementById("verify-code-1")?.focus();
    resetResend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resendResult]);

  useEffect(() => {
    if (!resendError) return;
    showToast(resendError.message, "error");
    resetResend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resendError]);

  // ---- Verify outcome. The session itself is committed by useVerifyEmail; this finishes the screen.
  const signedIn = useRef(false);
  // The effect below re-runs while the onboarding hand-off settles; the event is once per reply.
  const trackedVerifyResult = useRef<typeof verifyResult>(undefined);
  const hasSession = hasVerifiedSession(verifyResult);
  useEffect(() => {
    if (!verifyResult) return;
    if (trackedVerifyResult.current !== verifyResult) {
      trackedVerifyResult.current = verifyResult;
      // Outcome events go straight through trackEvent: the plan maps both to the Verify
      // button, and the page tracker would send the pair on every result.
      trackEvent("email_verified", {});
    }
    if (hasVerifiedSession(verifyResult)) {
      // Identity and the login event go first — an Esc during the wait below must not lose them.
      if (!signedIn.current) {
        signedIn.current = true;
        afterLogin(verifyResult.data);
        showToast(verifyResult.message, "success");
      }
      // The sign-up hand-off must land before this unmounts, or its user-properties effect never runs.
      if (isSubmittingOnboarding) return;
      hideModal();
      return;
    }
    setEmailValid(true);
    setMessageForLoginModal(verifyResult.message);
    setScreenIndex(AuthModalScreen.EnterEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyResult, isSubmittingOnboarding]);

  useEffect(() => {
    if (!verifyError) return;
    trackEvent("email_verification_failed", {
      error_type: verificationErrorType(verifyError),
    });
    if (isVerifyEmailCodeError(verifyError)) {
      setCodeError(
        verifyError.code === "invalid_code"
          ? i18n.get("incorrectVerificationCode")
          : verifyError.message,
      );
      if (verifyError.code !== "invalid_code") setCode("");
      // Land on the first cell so the retry starts where the error is.
      document.getElementById("verify-code-1")?.focus();
      return;
    }
    showToast(verifyError.message, "error");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyError]);

  // Verify and resend are exclusive: a verify racing a resend would spend an attempt
  // of the new code on the old digits. Once a session is in hand nothing may be re-sent.
  const isRequesting = isVerifying || isResending || hasSession;

  const submitCode = (event?: SyntheticEvent) => {
    event?.preventDefault();
    if (code.length !== VERIFICATION_CODE_LENGTH || isRequesting) return;
    setCodeError(undefined);
    verifyUserEmail({ token: code, email, login: true });
  };

  const handleResend = () => {
    if (countdown > 0 || isRequesting) return;
    verifyEmailAnalytics.track("resend_code_link_button", {});
    setCodeError(undefined);
    resendVerifyUserEmail({ email });
  };

  const handleReturnToLogin = () => {
    verifyEmailAnalytics.track("return_to_login_link_button", {
      entry_point: "login",
      entry_page: entryPageFromCurrentPage(),
    });
    setScreenIndex(AuthModalScreen.EnterEmail);
  };

  // Focus the title on mount, as the sibling auth screens do.
  const titleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  const resendLabel =
    countdown > 0
      ? i18n.get("resendAvailableIn").replace("{seconds}", String(countdown))
      : i18n.get(isCodeMode ? "resendCode" : "resendEmail");

  const isBusy = isRequesting || isSubmittingOnboarding;

  // The shell must not let Esc / backdrop unmount this mid-request: the verify reply commits a
  // session whose identity, event and onboarding flag only this component can run.
  useEffect(() => {
    setBusy(isBusy);
    return () => setBusy(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBusy]);

  const handleClose = () => {
    if (isBusy) return;
    // Same event the modal shell fires for backdrop / Esc (AuthModals), so ✕ counts too.
    trackEvent("auth_form_closed", { auth_screen: "email_verification" });
    hideModal();
  };

  return (
    <section
      style={modalStyle}
      className="relative h-full bg-bg-brand-secondary-default rounded-xl flex flex-col overflow-y-auto"
      aria-busy={isBusy}
    >
      {!isSignup && (
        <header
          className={`py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"}`}
        >
          <div className="flex-shrink-0 w-10" aria-hidden="true" />
          <h1
            id="modal-title"
            ref={titleRef}
            tabIndex={-1}
            className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4"
          >
            {i18n.get("verifyYourEmail")}
          </h1>
          <StandardButton
            onClick={handleClose}
            variant="closeModal"
            icon={
              <XIcon
                size={24}
                color={isDark ? "#A39FE8" : "#352FC3"}
                aria-hidden="true"
              />
            }
            aria-label="Close modal"
            className="flex-shrink-0"
            disabled={isBusy}
          />
        </header>
      )}

      <form
        className="flex-1 flex flex-col"
        onSubmit={submitCode}
        noValidate
      >
        <div className="flex-1 px-6 pt-6 flex flex-col gap-4">
          {isSignup ? (
            <h1
              id="modal-title"
              ref={titleRef}
              tabIndex={-1}
              className="title-l-bold text-fg-neutral-strong"
            >
              {i18n.get("finalStepVerifyYourEmail")}
            </h1>
          ) : null}

          <p className="body-m-medium text-fg-neutral-subtle -mt-2">
            {i18n.get("wehaveJustSentAnEmailTo")}
          </p>

          <div className="flex items-center gap-2 min-h-[48px] px-4 py-3 rounded-lg bg-container-brand-minimal">
            <EnvelopeOpenIcon
              size={24}
              aria-hidden="true"
              focusable="false"
              className="shrink-0 text-fg-brand-subtle"
            />
            <span className="label-m-medium text-fg-neutral-strong px-1 break-all">
              {email}
            </span>
          </div>

          <p
            id="verify-code-label"
            className="body-m-medium text-fg-neutral-subtle"
          >
            {i18n.get("clickTheLinkInsideToLogInToSociability")}
          </p>

          {isCodeMode && (
            <VerificationCodeInput
              id="verify-code"
              value={code}
              onChange={(next) => {
                setCode(next);
                if (codeError) setCodeError(undefined);
              }}
              error={codeError}
              disabled={isRequesting}
              labelledBy="verify-code-label"
              digitLabel={i18n.get("verificationCodeDigitLabel")}
            />
          )}

          <button
            type="button"
            onClick={handleResend}
            disabled={countdown > 0 || isRequesting}
            className="self-start min-h-[44px] flex items-center label-m-medium text-fg-brand-subtle underline focus-blue disabled:no-underline disabled:text-fg-neutral-subtle disabled:cursor-not-allowed"
          >
            {resendLabel}
          </button>

          {isBusy && <Loader />}
        </div>

        <div className="sticky bottom-0 p-4 bg-bg-brand-secondary-default">
          {isCodeMode ? (
            <WideButton
              buttonType="submit"
              disabled={
                code.length !== VERIFICATION_CODE_LENGTH || isRequesting
              }
            >
              {i18n.get("verifyEmail")}
            </WideButton>
          ) : (
            <WideButton onClick={handleReturnToLogin}>
              {i18n.get("returnToLoginPage")}
            </WideButton>
          )}
        </div>
      </form>
    </section>
  );
};

export default VerifyEmailComponent;
